import React, { useRef, useState } from "react";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import NotificationsList from "../../components/UI/NotificationList";
import { useNotifications } from "../../hooks/use-notifications";

const MONO_FONT = "'Noto Sans Mono',Consolas,'Courier New',monospace";

const EmailSignature: React.FC = () => {
  const [name, setName] = useState("박예승");
  const [role, setRole] = useState("기술팀 팀장");
  const [mobile, setMobile] = useState("010-2601-9948");
  const [web, setWeb] = useState("www.datoz.co.kr");
  const [email, setEmail] = useState("");

  const sigRef = useRef<HTMLDivElement>(null);
  const { notifications, addItem: addNotification } = useNotifications();

  const webDisplay = web
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");
  const trimmedEmail = email.trim();

  const copySignature = async () => {
    const sigEl = sigRef.current;
    if (!sigEl) return;
    try {
      const htmlBlob = new Blob([sigEl.innerHTML], { type: "text/html" });
      const textBlob = new Blob([sigEl.innerText], { type: "text/plain" });
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": htmlBlob, "text/plain": textBlob }),
      ]);
    } catch {
      // Clipboard API 미지원/권한 거부 시 selection 기반 폴백
      const range = document.createRange();
      range.selectNode(sigEl);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      document.execCommand("copy");
      selection?.removeAllRanges();
    }
    addNotification("서명이 복사되었습니다.");
  };

  const inputClassName =
    "font-normal normal-case tracking-normal text-sm text-[#1b2330] border border-[#e6e9ee] rounded-[9px] px-3 py-[10px] outline-none focus:border-[#4D79B5]";
  const labelClassName =
    "flex flex-col gap-[6px] text-[11px] font-bold text-[#8a93a2] uppercase tracking-wide text-left";

  return (
    <Pod24AdminLayout>
      <NotificationsList notifications={notifications} />

      <div className="max-w-[760px]">
        {/* 헤더 */}
        <div
          className="text-[11px] tracking-[0.14em] text-[#4D79B5] uppercase"
          style={{ fontFamily: MONO_FONT }}
        >
          DATOZ · EMAIL SIGNATURE
        </div>
        <h1 className="text-2xl font-extrabold text-[#1b2330] mt-2 mb-1.5">
          이메일 서명
        </h1>
        <p className="text-sm text-[#5b6472] leading-relaxed mb-7">
          각자 아래 칸에 <b>이름·직책·연락처</b>를 입력하면 서명이 실시간으로
          바뀝니다. <b>서명 복사</b> 후 Gmail·Outlook 등의 서명 설정 입력란에
          붙여넣으세요. 로고는 호스팅 URL로 포함되어 별도 업로드가 필요
          없습니다.
        </p>

        {/* 입력 폼 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-[18px] gap-y-[14px] bg-white border border-[#e6e9ee] rounded-[14px] px-6 py-[22px] mb-6">
          <label className={labelClassName}>
            이름
            <input
              className={inputClassName}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className={labelClassName}>
            직책 / 팀
            <input
              className={inputClassName}
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </label>
          <label className={labelClassName}>
            휴대폰
            <input
              className={inputClassName}
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
            />
          </label>
          <label className={labelClassName}>
            웹사이트
            <input
              className={inputClassName}
              value={web}
              onChange={(e) => setWeb(e.target.value)}
            />
          </label>
          <label className={`${labelClassName} sm:col-span-2`}>
            <span>
              이메일{" "}
              <span className="font-normal normal-case tracking-normal text-[#b0b8c2]">
                (선택 · 입력 시 서명에 표시)
              </span>
            </span>
            <input
              className={inputClassName}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="예: yspark@datoz.co.kr"
            />
          </label>
        </div>

        {/* 디자인 미리보기 */}
        <div
          className="text-[10px] tracking-[0.14em] text-[#9aa3b0] uppercase mb-3"
          style={{ fontFamily: MONO_FONT }}
        >
          디자인 미리보기
        </div>
        <div ref={sigRef} className="inline-block max-w-full overflow-x-auto">
          <table
            cellPadding={0}
            cellSpacing={0}
            role="presentation"
            style={{
              borderCollapse: "collapse",
              background: "#ffffff",
              border: "1px solid #e6e9ee",
            }}
          >
            <tbody>
              <tr>
                <td style={{ padding: "26px 40px" }}>
                  <table
                    cellPadding={0}
                    cellSpacing={0}
                    role="presentation"
                    style={{ borderCollapse: "collapse" }}
                  >
                    <tbody>
                      <tr>
                        <td valign="middle" style={{ paddingRight: 30 }}>
                          <img
                            src="https://api.pod24.co.kr/api/public/resource/datoz_logo_text_B.png"
                            alt="Datoz"
                            height={66}
                            style={{
                              display: "block",
                              border: 0,
                              outline: "none",
                              textDecoration: "none",
                              height: 66,
                              width: "auto",
                            }}
                          />
                        </td>
                        <td
                          valign="middle"
                          style={{
                            borderLeft: "1px solid #eceff3",
                            paddingLeft: 30,
                            fontFamily:
                              "'Noto Sans KR','Noto Sans',Arial,sans-serif",
                          }}
                        >
                          <div
                            style={{
                              fontFamily: MONO_FONT,
                              fontSize: 10,
                              letterSpacing: 2.5,
                              color: "#9aa3b0",
                              paddingBottom: 10,
                              textTransform: "uppercase",
                            }}
                          >
                            SMB 경쟁력 파트너{" "}
                            <span style={{ color: "#4D79B5", fontWeight: 500 }}>
                              No.1
                            </span>
                          </div>
                          <div style={{ paddingBottom: 12 }}>
                            <span
                              style={{
                                fontSize: 22,
                                fontWeight: "bold",
                                color: "#1b2330",
                                letterSpacing: -0.3,
                              }}
                            >
                              {name || "이름"}
                            </span>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: "#6b7480",
                              }}
                            >
                              {"\u00A0\u00A0"}
                              {role}
                            </span>
                            <span style={{ fontSize: 12, color: "#c2c8d0" }}>
                              {"\u00A0·\u00A0"}
                            </span>
                            <span style={{ fontSize: 12, color: "#9aa3b0" }}>
                              (주)데이토즈
                            </span>
                          </div>
                          <div style={{ lineHeight: 1 }}>
                            <span
                              style={{
                                display: "inline-block",
                                marginRight: 24,
                                whiteSpace: "nowrap",
                                verticalAlign: "middle",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: MONO_FONT,
                                  fontSize: 9.5,
                                  letterSpacing: 1.2,
                                  color: "#b0b8c2",
                                  marginRight: 8,
                                }}
                              >
                                MOBILE
                              </span>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#3a4250",
                                }}
                              >
                                {mobile}
                              </span>
                            </span>
                            {trimmedEmail && (
                              <span
                                style={{
                                  display: "inline-block",
                                  marginRight: 24,
                                  whiteSpace: "nowrap",
                                  verticalAlign: "middle",
                                }}
                              >
                                <span
                                  style={{
                                    fontFamily: MONO_FONT,
                                    fontSize: 9.5,
                                    letterSpacing: 1.2,
                                    color: "#b0b8c2",
                                    marginRight: 8,
                                  }}
                                >
                                  EMAIL
                                </span>
                                <a
                                  href={`mailto:${trimmedEmail}`}
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: "#3a4250",
                                    textDecoration: "none",
                                  }}
                                >
                                  {trimmedEmail}
                                </a>
                              </span>
                            )}
                            <span
                              style={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                                verticalAlign: "middle",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: MONO_FONT,
                                  fontSize: 9.5,
                                  letterSpacing: 1.2,
                                  color: "#b0b8c2",
                                  marginRight: 8,
                                }}
                              >
                                WEB
                              </span>
                              <a
                                href={`https://${webDisplay}`}
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#3c67a0",
                                  textDecoration: "none",
                                }}
                              >
                                {webDisplay}
                              </a>
                            </span>
                          </div>
                          <div
                            style={{
                              marginTop: 12,
                              fontSize: 11,
                              color: "#aeb6c0",
                              letterSpacing: 0.1,
                            }}
                          >
                            서울특별시 금천구 디지털로9길 99 스타밸리 805호
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 서명 복사 */}
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={copySignature}
            className="bg-[#4D79B5] hover:bg-[#3f6aa3] text-white text-[13px] font-semibold rounded-[10px] px-5 py-[11px]"
          >
            서명 복사
          </button>
        </div>

        <p className="text-[12.5px] text-[#8a93a2] leading-[1.7] mt-5">
          <b className="text-[#5b6472]">붙여넣기 팁</b>
          <br />
          · Gmail: 설정 → 모든 설정 보기 → 서명 → 새로 만들기 → 입력란에{" "}
          <b className="text-[#5b6472]">서명 복사</b> 결과를 붙여넣기
          <br />
          · Outlook: 파일 → 옵션 → 메일 → 서명 → 새로 만들기 → 붙여넣기
          <br />
          · 로고는 호스팅 URL로 삽입되어 대부분의 메일에서 바로 표시됩니다.
          (외부 이미지 표시 허용 필요)
        </p>
      </div>
    </Pod24AdminLayout>
  );
};

export default EmailSignature;
